<template>
<div>
    <!--#region delete modal-->
    <b-modal id="modaledit" title-class="modalTitle" title="Deleting a Domain" @hidden="Deleting = null" centered hide-footer no-fade :visible="Deleting !== null">
        <p>You are about to delete {{ Deleting ? Deleting.StoreName : "" }}. You will not be able to undo this action. Are you sure?</p>
        <!-- Emulate built in modal footer ok and cancel button actions -->
        <div class="row mb-2 float-right">
            <div class="col pr-0">
                <b-button class="float-right echeckpointBlue modalBtn" size="sm" @click="Deleting = null">Cancel</b-button>
            </div>
            <div class="col-auto">
                <b-button type="button" class="float-right userButton modalBtn" size="sm" @click="DeleteRow(Deleting)">Confirm</b-button>
            </div>
        </div>
    </b-modal>
    <!--#endregion-->
    
    <div class="groupSet card">
        <h4 class="group-title">BigCommerce</h4>
        <!--#region table-->
        <div v-if="!Editing">
            <table class="table table-centered mb-0">
                <thead>
                    <tr>
                        <th>Store Name</th>
                        <th>Store Hash</th>
                        <th>Channel ID</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="settings in BCSettingsList" :key="settings.ID">
                        <td class="tableText">{{ settings.StoreName }}</td>
                        <td class="tableText">{{ settings.Hash }}</td>
                        <td class="tableText">{{ settings.StoreChannelID }}</td>
                        <td>
                            <button class="btnContainer" @click="SelectEdit(settings)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                            <button class="btnContainer" @click="ConfirmDelete(settings)" title="Delete"><i class="mdi mdi-delete"></i></button>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="3"></td>
                        <td><button class="btnContainer" @click="NewStore()" title="Add New"><i class="mdi mdi-plus-circle"></i></button></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!--#endregion-->
        <form id="bcForm" @submit="UpdateBC($event)" v-if="Editing">
            <!--#region Store Data-->
            <label for="StoreName">Store Name:</label>
            <input id="StoreName" type="text" class="form-control" v-model="TempBCSettings.StoreName"><br>
            <label for="StoreHash">Store Hash:</label>
            <input id="StoreHash" v-if="Adding" type="text" class="form-control" v-model="TempBCSettings.Hash">
            <span id="StoreHash" v-if="!Adding">{{ TempBCSettings.Hash }}</span><br>
            <label for="BCAccessToken">BigCommerce API Token:</label>
            <input id="BCAccessToken" v-if="Adding" type="text" class="form-control" v-model="TempBCSettings.BCAccessToken">
            <span id="BCAccessToken" v-if="!Adding">{{ TempBCSettings.BCAccessToken }}</span><br>
            <label for="StoreChannelID">Channel ID:</label>
            <!-- Editing existing: read-only display -->
            <span v-if="!Adding" id="StoreChannelID">{{ TempBCSettings.StoreChannelID }}</span>
            <!-- Adding new: smart channel selector -->
            <template v-if="Adding">
                <!-- Default state: read-only "1" with toggle link (channel 1 available) -->
                <template v-if="!ShowChannelPicker && !BCChannelsError && !Channel1AlreadyTaken">
                    <span id="StoreChannelID" class="form-control-plaintext d-inline">{{ TempBCSettings.StoreChannelID || "1" }}</span>
                    <a v-if="!ChannelsLoading && TempBCSettings.Hash && TempBCSettings.BCAccessToken" href="#" class="ml-2" style="font-size: 0.85em;" @click.prevent="LoadChannels()">
                        Configure for a different channel?
                    </a>
                    <span v-if="ChannelsLoading" class="ml-2 text-muted" style="font-size: 0.85em;">Loading channels...</span>
                    <div v-if="ChannelsLoaded && !BCChannelsError && BCChannels.length === 1" class="text-muted mt-1" style="font-size: 0.85em;">
                        This store has one channel.
                    </div>
                    <div v-if="ChannelsLoaded && !BCChannelsError && AvailableChannels.length === 0 && BCChannels.length > 1" class="text-info mt-1" style="font-size: 0.85em;">
                        All channels for this store are already configured.
                    </div>
                </template>
                <!-- Channel 1 already taken for this hash: must pick a different channel -->
                <template v-if="!ShowChannelPicker && !BCChannelsError && Channel1AlreadyTaken">
                    <input id="StoreChannelID" type="text" class="form-control" v-model="TempBCSettings.StoreChannelID" placeholder="Enter Channel ID">
                    <a v-if="!ChannelsLoading && TempBCSettings.Hash && TempBCSettings.BCAccessToken" href="#" class="ml-2" style="font-size: 0.85em;" @click.prevent="LoadChannels()">
                        Load available channels
                    </a>
                    <span v-if="ChannelsLoading" class="ml-2 text-muted" style="font-size: 0.85em;">Loading channels...</span>
                    <div class="text-info mt-1" style="font-size: 0.85em;">
                        Channel 1 is already configured for this store. Enter or select a different Channel ID.
                    </div>
                </template>
                <!-- Dropdown mode: channels loaded successfully with multiple options -->
                <template v-if="ShowChannelPicker && AvailableChannels.length > 0">
                    <select id="StoreChannelID" class="form-control" v-model="TempBCSettings.StoreChannelID">
                        <option v-for="ch in AvailableChannels" :key="ch.id" :value="String(ch.id)">{{ ch.name }} ({{ ch.id }})</option>
                    </select>
                </template>
                <!-- Fallback: manual text input when channel API call failed (missing scope) -->
                <template v-if="BCChannelsError">
                    <input id="StoreChannelID" type="text" class="form-control" v-model="TempBCSettings.StoreChannelID">
                    <div class="text-warning mt-1" style="font-size: 0.85em;">
                        {{ BCChannelsError }} You can enter the Channel ID manually.
                    </div>
                </template>
            </template>
            <br>
            <br><hr>
            <!--#endregion-->

            <!--#region PreOrder-->
            <h4 class="header-title">Pre-Order Checkpoint</h4>
            <p>The Pre-Order Checkpoint reviews items in the shopping cart once all the necessary information has been provided<!--when the Place Order button is clicked and before payment is processed-->.
            It blocks non-compliant items from being purchased and helps prevent refunds.
            For full compliance and accuracy, this checkpoint must be used in conjunction with the Post-Order Checkpoint.</p>
            <h5>General Settings</h5>
            <p>Configure the settings for your pre-order checkpoint.</p>
            <label for="PreFlow">Checkpoint: </label>
            <select class="form-control" type="text" id="PreFlow" name="PreFlow" v-model="TempBCSettings.SubscriberServiceFlowIDPreCheckout">
                <option v-for="flow in PreOrderFlows" :key="flow.Id" :value="flow.Id">{{ flow.Name }}</option>
            </select><br>
            <label>Enable Pre-Order Check:</label>
            <div class="OptionSwitch">
                <div>
                    <input id="PreCheckoutEnabled" type="checkbox" v-model="TempBCSettings.PreCheckoutEnabled" data-switch="success" />
                    <label for="PreCheckoutEnabled"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <label>Show Customer Type Options:</label>
            <div class="OptionSwitch">
                <div>
                    <input id="DisplayCustomerType" type="checkbox" v-model="TempBCSettings.DisplayCustomerType" data-switch="success" />
                    <label for="DisplayCustomerType"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <label>Show License Finder Map:</label>
            <div class="OptionSwitch">
                <div>
                    <input id="DisplayLicenseMap" type="checkbox" v-model="TempBCSettings.DisplayLicenseMap" data-switch="success" />
                    <label for="DisplayLicenseMap"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <h5>Conditional Fail Settings</h5>
            <p>Displays a message during checkout if additional information will be required from your customer after checkout for example, permits or licenses.
            Displaying these messages will enable your customers to remove products from their cart that they are not qualified to purchase.</p>
            <label>Show Conditional Fail Messages:</label>
            <div class="OptionSwitch">
                <div>
                    <input id="DisplayCondlFail" type="checkbox" v-model="TempBCSettings.DisplayCondlFail" data-switch="success" />
                    <label for="DisplayCondlFail"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <h5>Notification Banner Anchor Settings</h5>
            <p>Choose where the compliance status notification banner should appear on your BigCommerce checkout page. 
                The banner will display directly below the HTML &lt;div&gt; element you specify.<br/>
                To set the anchor point, enter the type of selector (such as id or class) and its corresponding 
                value (e.g., CheckoutShippingAddress, .custom-container, etc.) below.</p>
            <label for="AnchorType">Type: </label>
            <select class="form-control" type="text" id="AnchorType" name="AnchorType" v-model="TempBCSettings.AnchorElementTypeID">
                <option v-for="anchorType in AnchorElementTypes" :key="anchorType.Id" :value="anchorType.Id">{{ anchorType.Name }}</option>
            </select><br>
            <label for="AnchorElement">Value:</label>
            <input id="AnchorElement" type="text" class="form-control" v-model="TempBCSettings.AnchorElementValue"><br>
            <label for="AnchorPosition">Position:</label>
            <select class="form-control" type="text" id="AnchorPosition" name="AnchorPosition" v-model="TempBCSettings.AnchorElementPositionID">
                <option v-for="anchorPosition in AnchorElementPositions" :key="anchorPosition.Id" :value="anchorPosition.Id">{{ anchorPosition.Name }}</option>
            </select><br>
            <br><hr>
            <!--#endregion-->
            
            <!--#region ClearShop-->
            <h4 class="header-title">ClearShop</h4>
            <p>Allow customers to provide an address and get compliance results before even adding an item to their cart.</p>
            <label>Enable ClearShop: </label>
            <div class="OptionSwitch">
                <div>
                    <input id="ClearShopEnabled" type="checkbox" v-model="TempBCSettings.ClearShopEnabled" data-switch="success" />
                    <label for="ClearShopEnabled"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>

            <!-- Shown when ClearShop is enabled: shared location bar, restricted prefix, and cache control -->
            <template v-if="TempBCSettings.ClearShopEnabled">
                <label for="ClrFlow">Checkpoint: </label>
                <select class="form-control" type="text" id="ClrFlow" name="ClrFlow" v-model="TempBCSettings.SubscriberServiceFlowIDClearShop">
                    <option v-for="flow in ClearShopFlows" :key="flow.Id" :value="flow.Id">{{ flow.Name }}</option>
                </select><br>
                <h5>Authorization</h5>
                <p>The storefront domain is used to help authorize ClearShop checks.</p>
                <label for="AllowedDomain">Authorized Domain:</label>
                <input id="AllowedDomain" type="text" class="form-control" v-model="TempBCSettings.AllowedDomain" placeholder="store.example.com"><br>

                <div class="clearshop-messages">
                    <h5>Messaging</h5>
                    <p>Customize the storefront messaging shown by ClearShop. Leave a field blank to use the built-in default (shown as placeholder text).</p>

                    <label for="RestrictedPrefix">Restriction Message Prefix:</label>
                    <textarea id="RestrictedPrefix" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.RestrictedPrefix" placeholder="Cannot be shipped to"></textarea>
                    <p style="font-size: 0.85em;">Used on both the product page and product list.</p>

                    <h5>Location Bar Messages</h5>
                    <label for="LocationBarPromptText">Location Prompt:</label>
                    <textarea id="LocationBarPromptText" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.LocationBarPromptText" placeholder="Set your location to check shipping availability"></textarea><br>
                    <label for="LocationBarHeaderText">Location Bar Heading:</label>
                    <textarea id="LocationBarHeaderText" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.LocationBarHeaderText" placeholder="Displaying shipping restrictions for"></textarea><br>
                    <label for="LocationBarExcludedText">Excluded Location Text:</label>
                    <textarea id="LocationBarExcludedText" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.LocationBarExcludedText" placeholder="Shipping restrictions check not available for"></textarea><br>
                </div>

            <!-- Product Page (PDP) messages:-->
                <div class="clearshop-messages">
                    <h5>Product Page Messages</h5>
                    <label for="PdpMessageSuccess">Success Message:</label>
                    <textarea id="PdpMessageSuccess" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageSuccess" placeholder="This product can be shipped to this address."></textarea><br>
                    <label for="PdpMessageConditional">Conditional Message:</label>
                    <textarea id="PdpMessageConditional" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageConditional" placeholder="More information may be required for this purchase after checkout."></textarea><br>
                    <label for="PdpMessageFflConsumer">FFL Consumer Message:</label>
                    <textarea id="PdpMessageFflConsumer" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageFflConsumer" placeholder="You may be able to purchase this item but it requires delivery to and verification by a Dealer."></textarea><br>
                    <label for="PdpMessageFflB2bProvided">FFL Dealer Provided Message:</label>
                    <textarea id="PdpMessageFflB2bProvided" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageFflB2bProvided" placeholder="This item can be shipped to this address but more information may be required after checkout."></textarea><br>
                    <label for="PdpMessageFflB2bNotProvided">FFL Dealer Not Provided Message:</label>
                    <textarea id="PdpMessageFflB2bNotProvided" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageFflB2bNotProvided" placeholder="This item requires delivery to your Business Premise Address."></textarea><br>
                    <label for="PdpMessageCatalogError">Catalog Error Message:</label>
                    <textarea id="PdpMessageCatalogError" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpMessageCatalogError" placeholder="Unable to verify shipping restrictions for this item."></textarea><br>
                    <label for="PdpExcludedText">Excluded State Text:</label>
                    <textarea id="PdpExcludedText" class="form-control" rows="2" :maxlength="MessageMaxLength" v-model="TempBCSettings.PdpExcludedText" placeholder="Shipping restriction information is not available for your state."></textarea><br>
                </div>
                <label>Clear cache on save:</label>
                <div class="OptionSwitch">
                    <div>
                        <input id="ClearCacheOnSave" type="checkbox" v-model="ClearCacheOnSave" data-switch="success" />
                        <label for="ClearCacheOnSave" data-on-label="On" data-off-label="Off"></label>
                    </div>
                </div><br>
                <label>Cache last cleared:</label>
                <span>{{ TempBCSettings.CacheCleared ? formatDateLocal(TempBCSettings.CacheCleared) : "Never" }}</span><br>
            </template>
            <br><hr>
            <!--#endregion-->

            <!--#region PostOrder-->
            <h4 class="header-title">Post-Order Checkpoint</h4>
            <p>The Post-Order Checkpoint reviews the items in the shopping cart after the order has been placed.
            This provides a tamper-proof compliance record, ensuring it is the most reliable source for Customer Service to confirm final
            actions needed to process the order.</p>
            <label for="PostFlow">Checkpoint: </label>
            <select class="form-control" type="text" id="PostFlow" name="PostFlow" v-model="TempBCSettings.SubscriberServiceFlowIDPostCheckout">
                <option v-for="flow in PostOrderFlows" :key="flow.Id" :value="flow.Id">{{ flow.Name }}</option>
            </select><br>
            <h5>Order Comment Settings</h5>
            <p>Adds a comment to each order noting the compliance check result. Your customers will not be notified.</p>
            <label>Add Order Comment: </label>
            <div class="OptionSwitch">
                <div>
                    <input id="Comments" type="checkbox" v-model="TempBCSettings.OrderComment" data-switch="success" />
                    <label for="Comments"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <h5>Order Status Settings</h5>
            <p>Places orders with a Fail or Conditional Fail result on hold. Your customers will not be notified.</p>
            <label>Place Orders On Hold: </label>
            <div class="OptionSwitch inline">
                <div>
                    <input id="Statuses" type="checkbox" v-model="TempBCSettings.OrderStatusUpdate" data-switch="success" />
                    <label for="Statuses"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>

            <h5>Specify Order Status for Compliance Fail</h5>
            <p>When enabled, orders that fail compliance will have their status set to the configured fail status below. When disabled, orders that fail compliance will be set to "Manual Verification Required".</p>
            <label>Specify Order Status for Compliance Fail: </label>
            <div class="OptionSwitch inline">
                <div>
                    <input id="failStatusEnabled" type="checkbox" v-model="TempBCSettings.FailStatusEnabled" data-switch="success" />
                    <label for="failStatusEnabled"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <label for="failStatusId">Order Status for Compliance Fail: </label>
            <select class="form-control" type="text" id="failStatusId" name="failStatusId" v-model="TempBCSettings.FailStatusId">
                <option v-for="status in Statuses" :key="status.id" :value="Number(status.id)">{{ status.name }}</option>
            </select><br>
            <h5>Specify Order Status for Compliance Pass</h5>
            <p>When enabled, orders that pass compliance will have their status set to the configured pass status below. This applies to both immediate passes and remediated orders. When disabled, status will be set to "Awaiting Fulfillment".</p>
            <label>Specify Order Status for Compliance Pass: </label>
            <div class="OptionSwitch inline">
                <div>
                    <input id="passStatusEnabled" type="checkbox" v-model="TempBCSettings.PassStatusEnabled" data-switch="success" />
                    <label for="passStatusEnabled"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <label for="passStatusId">Order Status for Compliance Pass: </label>
            <select class="form-control" type="text" id="passStatusId" name="passStatusId" v-model="TempBCSettings.PassStatusId">
                <option v-for="status in Statuses" :key="status.id" :value="Number(status.id)">{{ status.name }}</option>
            </select><br>
            <h5>Compliance Check Trigger</h5>
            <p>Choose when compliance checks run: immediately after an order is placed, or only when an order reaches one of the selected statuses.</p>
            <label for="postOrderTriggerMode">Trigger Mode: </label>
            <select class="form-control" id="postOrderTriggerMode" name="postOrderTriggerMode" v-model="TempBCSettings.PostOrderTriggerMode">
                <option :value="0">Immediate (after order placement)</option>
                <option :value="1">When order reaches status</option>
            </select><br>
            <div v-if="TempBCSettings.PostOrderTriggerMode === 1">
                <label for="PostOrderTriggerStatuses">Trigger Status(es):</label>
                <multiselect
                    class="select"
                    v-model="TempBCSettings.PostOrderTriggerStatuses"
                    placeholder="Search for Statuses"
                    ref="PostOrderTriggerStatuses"
                    :hide-selected="true"
                    label="name"
                    track-by="id"
                    :options="Statuses"
                    :multiple="true"
                    :internal-search="true">
                    <template #noResult>
                        <span>Oops! No elements found. Consider changing the search query.</span>
                    </template>
                </multiselect><br>
            </div>
            <br><hr>
            <!--#endregion-->

            <!--#region StatusGuard-->
            <h4 class="header-title">Status Guard Settings</h4>
            <p>Prevents orders from leaving the fail status until compliance requirements are met. When other systems attempt to change an order status away from the fail status, the order will be reverted back if requirements are still pending.</p>

            <label>Enable Order Status Guard: </label>
            <div class="OptionSwitch">
                <div>
                    <input id="StatusGuard" type="checkbox" v-model="TempBCSettings.StatusGuard" data-switch="success" />
                    <label for="StatusGuard"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>

            <label for="AllowedStatuses">Allowed Statuses:</label>
            <p>Statuses that bypass the guard. Orders can be changed to these statuses even when compliance is pending. The defaults are Refunded, Canceled, Declined, Disputed</p>
            <multiselect 
                class="select" 
                v-model="TempBCSettings.AllowedStatuses" 
                placeholder="Search for Statuses"
                ref="AllowedStatuses" 
                :hide-selected="true" 
                label="name"
                track-by="id" 
                :options="Statuses"
                :multiple="true" 
                :internal-search="true">
                
                <template #noResult>
                    <span>Oops! No elements found. Consider changing the search query.</span>
                </template>
            </multiselect><br>
            <br><hr>
            <!--#endregion-->

            <!--#region AutoCancel-->
            <h4 class="header-title">Auto-Cancel Settings</h4>
            <h5>Auto-Cancel</h5>
            <p>
                When enabled, automatically cancels noncompliant items for: (1) orders that exceed the timeout
                period, (2) customers who opt out of verification, and (3) regulations failures. If payment has
                been captured, a refund will be attempted. If disabled, orders require manual review.
            </p>
            <label>Auto-Cancel Noncompliant Items: </label>
            <div class="OptionSwitch">
                <div>
                    <input id="AutoCancel" type="checkbox" v-model="TempBCSettings.AutoCancel" data-switch="success" />
                    <label for="AutoCancel"  data-on-label="On" data-off-label="Off"></label>
                </div>
            </div><br>
            <br><hr>
            <!--#endregion-->

            <!--#region ExcludedCustomerGroups-->
            <h4 class="header-title">Excluded Customer Groups Settings</h4>
            <p>Configure specific customer groups that are exempt from standard compliance validation.
                Orders placed by customers in the selected groups will automatically bypass restriction checks.</p>
            <p class="text-muted" style="font-size: 0.85em;"><strong>Note:</strong> At checkout (pre-order check), the bypass
                applies only when the customer is <strong>signed in</strong>; a customer's group cannot be determined for
                guest checkouts, so guests are always screened.</p>
            <label for="ExcludedCustomerGroups">Excluded Customer Groups:</label>
            <multiselect
                class="select"
                v-model="TempBCSettings.selectedExcludedCustomerGroups"
                placeholder="Search for Excluded Customer Groups"
                ref="ExcludedCustomerGroups"
                :hide-selected="true"
                label="name"
                track-by="id"
                :options="BCCustomerGroups"
                :multiple="true"
                :internal-search="true">

                <template #noResult>
                    <span>Oops! No elements found. Consider changing the search query.</span>
                </template>
            </multiselect>
            <div v-if="BCCustomerGroupsError" class="text-danger mt-1" style="font-size: 0.85em;">
                {{ BCCustomerGroupsError }}
            </div>
            <br>
            <br><hr>
            <!--#endregion-->

            <!--#region ExcludedStates-->
            <h4 class="header-title">Excluded States Settings</h4>
            <p>Configure specific states that are exempt from standard compliance validation. 
                Orders shipping to the selected states will automatically bypass restriction checks.</p>
            <label for="ExcludedStates">Excluded States:</label>
            <multiselect 
                class="select" 
                v-model="TempBCSettings.selectedExcludedStates" 
                placeholder="Search for Excluded States"
                ref="ExcludedStates" 
                :hide-selected="true" 
                label="name"
                track-by="name" 
                :options="ExcludedStates"
                :multiple="true" 
                :internal-search="true">
                
                <template #noResult>
                    <span>Oops! No elements found. Consider changing the search query.</span>
                </template>
            </multiselect><br>
            <br><hr>
            <!--#endregion-->

            <!--#region ComplianceFee-->
            <!-- REMOVED TEMPORARILY FOR TASK #27725 ON 03/12/25 
                <h4 class="header-title">Compliance Fee</h4>
                <p>Offset the costs of your compliance program by adding a compliance fee in checkout.</p>
                <label>Enable 50-State Compliance Fee: </label>
                <div class="OptionSwitch inline">
                    <div>
                        <input id="FeeOn" type="checkbox" v-model="TempBCSettings.Fee.Enabled" data-switch="success" />
                        <label for="FeeOn"  data-on-label="On" data-off-label="Off"></label>
                    </div>
                </div><br>
                <label>Fee Percentage: </label>
                <div class="percentage"><input type="number" v-model="TempBCSettings.Fee.Percent" class="form-control right-align" step=".01" min="0" max="100"></div><br>
                <label>Minimum Fee Per Item: </label>
                <div class="currency"><input type="number" v-model="TempBCSettings.Fee.Minimum" class="form-control right-align" step=".01" min="0"></div><br>
                <label>Maximum Fee Per Item: </label>
                <div class="currency"><input type="number" v-model="TempBCSettings.Fee.Maximum" class="form-control right-align" step=".01" min="0"></div><br>
                <label>Calculate Fee For up to X Items per SKU: </label>
                <input type="number" v-model="TempBCSettings.Fee.CountDuplicates" class="form-control right-align" min="0"><br>
            -->
            <!--#endregion-->
            <div id="SaveButton">
                <SaveButton  :handle="saveHandle" :state="saveState"/>
                <button class="btn echeckpointBlue" type="button" @click="Editing = false; Adding = false">Back</button>
            </div>
        </form>
    </div>
</div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SaveButton from "@/components/universal/saveButton";
import { nextTick } from 'vue';
import Multiselect from "vue-multiselect";
import { formatDateLocal } from "@/_helpers/DateHelper";


export default {
        page: {
        title: "BigCommerceApp",
    },
    components: {
        SaveButton,
        Multiselect
    },
    data() {
    return {
        saveState: null,
        ParentURL: null,
        Editing: null,
        Adding: false,
        Deleting: null,
        TempBCSettings: {
            Hash: "",
            BCAccessToken: "",
            StoreChannelID: null,
            SubscriberServiceFlowIDPreCheckout: null,
            SubscriberServiceFlowIDPostCheckout: null,
            SubscriberServiceFlowIDClearShop: null,
            OrderStatusUpdate: false,
            OrderComment: false,
            DisplayCondlFail: false,
            DisplayCustomerType: false,
            DisplayLicenseMap: false,
            PreCheckoutEnabled: false,
            Fee: {
                Enabled: false,
                Minimum: 0,
                Maximum: 0,
                Percent: 0,
                CountDuplicates: 0,
            },
            AnchorElementTypeID: null,
            AnchorElementValue: null,
            AnchorElementPositionID: null,
            ScriptUUID: null,
            selectedExcludedStates: [],
            selectedExcludedCustomerGroups: [],
            ExcludedCustomerGroups: null,
            ExcludedStates : null,
            StatusGuard: false,
            AllowedStatuses: null,
            AutoCancel: false,
            PassStatusEnabled: false,
            PassStatusId: null,
            FailStatusEnabled: false,
            FailStatusId: null,
            PostOrderTriggerMode: 0,
            PostOrderTriggerStatuses: null,
            ClearShopEnabled: false,
            CacheCleared: null,
            AllowedDomain: null,
            LocationBarPromptText: null,
            LocationBarHeaderText: null,
            LocationBarExcludedText: null,
            PdpMessageSuccess: null,
            PdpMessageConditional: null,
            PdpMessageFflConsumer: null,
            PdpMessageFflB2bProvided: null,
            PdpMessageFflB2bNotProvided: null,
            PdpMessageCatalogError: null,
            PdpExcludedText: null,
            RestrictedPrefix: null
        },
        Statuses: [
            {id:  "0", name: "Incomplete"},
            {id:  "1", name: "Pending"},
            {id:  "2", name: "Shipped"},
            {id:  "3", name: "Partially Shipped"},
            {id:  "4", name: "Refunded"},
            {id:  "5", name: "Cancelled"},
            {id:  "6", name: "Declined"},
            {id:  "7", name: "Awaiting Payment"},
            {id:  "8", name: "Awaiting Pickup"},
            {id:  "9", name: "Awaiting Shipment"},
            {id: "10", name: "Completed"},
            {id: "11", name: "Awaiting Fulfillment"},
            {id: "12", name: "Manual Verification Required"},
            {id: "13", name: "Disputed"},
            {id: "14", name: "Partially Refunded"},
        ],
        ChannelsLoaded: false,
        ChannelsLoading: false,
        ShowChannelPicker: false,
        MessageMaxLength: 255,
        ClearCacheOnSave: false,
    };
    },
    computed: {
        ...mapState('settings', ["BCSettings", "BCSettingsList", "Status", "Flows", "AnchorElementTypes", "AnchorElementPositions", "ExcludedStates", "BCChannels", "BCChannelsError", "BCCustomerGroups", "BCCustomerGroupsError"]),
        AvailableChannels()
        {
            if (!this.BCChannels || !this.BCChannels.length) return [];
            const usedChannelIds = (this.BCSettingsList || [])
                .filter(s => s.Hash === this.TempBCSettings.Hash && s.ID !== this.TempBCSettings.ID)
                .map(s => String(s.StoreChannelID));
            return this.BCChannels.filter(ch => !usedChannelIds.includes(String(ch.id)));
        },
        Channel1AlreadyTaken()
        {
            return (this.BCSettingsList || []).some(
                s => s.Hash === this.TempBCSettings.Hash && String(s.StoreChannelID) === "1" && s.ID !== this.TempBCSettings.ID
            );
        },
        PreOrderFlows()
        {
            const positionIDs = [2];
            return this.FlowsForPositions(positionIDs, (this.TempBCSettings || {}).SubscriberServiceFlowIDPreCheckout);
        },
        ClearShopFlows()
        {
            const positionIDs = [3];
            return this.FlowsForPositions(positionIDs, (this.TempBCSettings || {}).SubscriberServiceFlowIDClearShop);
        },
        PostOrderFlows()
        {
            const positionIDs = [1];
            return this.FlowsForPositions(positionIDs, (this.TempBCSettings || {}).SubscriberServiceFlowIDPostCheckout);
        }
    },
    watch: {
        Flows()
        {
            this.DefaultSingleFlowSelections();
        }
    },
    async created() {
        this.getSubFlows();
        this.getAnchorElementTypes();
        this.getAnchorElementPositions();
        this.getExcludedStates();
        await this.getAllBigCommerce();
    },
    async mounted() {
        await this.UpdateTemp();
        await this.getExcludedStates();
        if (this.BCSettings) {
            this.UpdateTemp(this.BCSettings);
        }
    },
  
    methods: {
        ...mapActions("settings", ["updateBigCommerce", "addBigCommerce", "deleteBigCommerce", "getAllBigCommerce", "getSubFlows", "GetScripts", "AddScript", "UpdateScript", "DeleteScript", "getAnchorElementTypes", "getAnchorElementPositions", "getExcludedStates", "getBigCommerceChannels", "getBigCommerceCustomerGroups"]),
        DefaultSingleFlowSelections()
        {
            if (!this.Adding || !this.TempBCSettings) return;
            if (this.TempBCSettings.SubscriberServiceFlowIDPreCheckout == null && this.PreOrderFlows.length === 1)
                this.TempBCSettings.SubscriberServiceFlowIDPreCheckout = this.PreOrderFlows[0].Id;
            if (this.TempBCSettings.SubscriberServiceFlowIDClearShop == null && this.ClearShopFlows.length === 1)
                this.TempBCSettings.SubscriberServiceFlowIDClearShop = this.ClearShopFlows[0].Id;
            if (this.TempBCSettings.SubscriberServiceFlowIDPostCheckout == null && this.PostOrderFlows.length === 1)
                this.TempBCSettings.SubscriberServiceFlowIDPostCheckout = this.PostOrderFlows[0].Id;
        },
        FlowsForPositions(positionIDs, selectedID)
        {
            const flows = Object.values(this.Flows || {});
            const wanted = (positionIDs || []).map(Number);
            const matching = wanted.length
                ? flows.filter(flow => wanted.includes(Number(flow.PositionID)) || String(flow.Id) === String(selectedID))
                : flows;
            return matching.sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
        },
        formatDateLocal,
        // Reduce a pasted URL/origin down to just the host (drops scheme, port, path, query, hash). Empty -> null.
        normalizeDomain(value)
        {
            if (!value) return null;
            let input = value.trim();
            if (!input) return null;
            // Ensure a scheme so the URL parser can extract the host.
            if (!/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(input))
                input = "https://" + input;
            try
            {
                return new URL(input).hostname;
            }
            catch (e)
            {
                // Fallback: strip scheme, then take everything up to the first / : ? # or whitespace.
                return value.trim().replace(/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//, "").split(/[/:?#\s]/)[0].toLowerCase() || null;
            }
        },
        async UpdateBC(ev)
        {
            ev.preventDefault();

            if(!this.ValidateFields())
            {
                this.saveState = "Missing required field";
                setTimeout(()=>{this.saveState = ""}, 5000);
                return;
            }
            const tooLong = this.MessageLengthErrors();
            if(tooLong.length > 0)
            {
                this.saveState = `${tooLong.join(", ")} ${tooLong.length === 1 ? "exceeds" : "exceed"} ${this.MessageMaxLength} characters`;
                setTimeout(()=>{this.saveState = ""}, 5000);
                return;
            }
            // Stamp the clear time when the user ticks "clear cache", and also on first save when it
            // was never set (CacheCleared is a non-nullable DateTime server-side, so null can't be sent).
            if(this.ClearCacheOnSave || !this.TempBCSettings.CacheCleared)
            {
                this.TempBCSettings.CacheCleared = new Date().toISOString(); // UTC (ISO 8601, Z)
            }
            this.TempBCSettings.AllowedDomain = this.normalizeDomain(this.TempBCSettings.AllowedDomain);
            this.saveState = "Saving";
            try
            {
                if(this.Adding)
                {
                    await this.addBigCommerce(this.ValidateSettings())
                }
                else
                    await this.updateBigCommerce(this.ValidateSettings())
                
                await this.getAllBigCommerce();
                if(this.Adding)
                {
                    const newSetting = this.BCSettingsList.find(x => x.Hash === this.TempBCSettings.Hash && x.StoreChannelID === (this.TempBCSettings.StoreChannelID || "1"));
                    this.TempBCSettings.ID = newSetting.ID;
                    this.Adding = false;
                }
                const newSettings = this.BCSettingsList.find(x => x.ID === this.TempBCSettings.ID);
                this.Editing = {...newSettings, Fee: {...newSettings.Fee}};
                
                this.ClearCacheOnSave = false;
                this.SetTimedSaveState("Saved");
            }
            catch (e)
            {
                this.SetTimedSaveState("Save Failed. The store hash or token may be invalid or a duplicate.");
            }
        },
        SetTimedSaveState(message)
        {
            this.saveState = message;
            setTimeout(()=>{this.saveState = ""}, 5000);
        },
        UpdateTemp(settings)
        {
            if(settings)
            {
                this.TempBCSettings.ID = settings.ID ?? null
                this.TempBCSettings.Hash = settings.Hash ?? null
                this.TempBCSettings.StoreName = settings.StoreName ?? null
                this.TempBCSettings.BCAccessToken = settings.BCAccessToken ?? null
                this.TempBCSettings.StoreChannelID = settings.StoreChannelID ?? null
                this.TempBCSettings.SubscriberServiceFlowIDPreCheckout = settings.SubscriberServiceFlowIDPreCheckout ?? null
                this.TempBCSettings.SubscriberServiceFlowIDPostCheckout = settings.SubscriberServiceFlowIDPostCheckout ?? null
                this.TempBCSettings.SubscriberServiceFlowIDClearShop = settings.SubscriberServiceFlowIDClearShop ?? null
                this.TempBCSettings.OrderStatusUpdate = settings.OrderStatusUpdate ?? null
                this.TempBCSettings.OrderComment = settings.OrderComment ?? null
                this.TempBCSettings.PreCheckoutEnabled = settings.PreCheckoutEnabled ?? null
                this.TempBCSettings.DisplayCondlFail = settings.DisplayCondlFail ?? null
                this.TempBCSettings.DisplayCustomerType = settings.DisplayCustomerType ?? null
                this.TempBCSettings.DisplayLicenseMap = settings.DisplayLicenseMap ?? null
                this.TempBCSettings.Fee = JSON.parse(JSON.stringify(settings.Fee)) ?? {Enabled: false, Minimum: 0, Maximum: 0, Percent: 0, CountDuplicates: 0}
                this.TempBCSettings.AnchorElementTypeID = settings.AnchorElementTypeID ?? null
                this.TempBCSettings.AnchorElementValue = settings.AnchorElementValue ?? null
                this.TempBCSettings.AnchorElementPositionID = settings.AnchorElementPositionID ?? null
                this.TempBCSettings.ScriptUUID = settings.ScriptUUID ?? null
                this.TempBCSettings.ExcludedStates = settings.ExcludedStates ?? null;
                this.TempBCSettings.selectedExcludedStates = this.toArray(this.TempBCSettings.ExcludedStates, this.ExcludedStates, "name");
                this.TempBCSettings.ExcludedCustomerGroups = settings.ExcludedCustomerGroups ?? null;
                this.TempBCSettings.selectedExcludedCustomerGroups = settings.ExcludedCustomerGroups ? this.toIDArray(settings.ExcludedCustomerGroups, this.BCCustomerGroups, "id") : [];
                this.TempBCSettings.StatusGuard = settings.StatusGuard ?? false;
                this.TempBCSettings.AllowedStatuses = this.toArray(settings.AllowedStatuses, this.Statuses, "id");
                this.TempBCSettings.AutoCancel = settings.AutoCancel ?? false;
                this.TempBCSettings.ClearShopEnabled = settings.ClearShopEnabled ?? false;
                this.TempBCSettings.CacheCleared = settings.CacheCleared ?? null;
                this.TempBCSettings.AllowedDomain = settings.AllowedDomain ?? null;
                this.TempBCSettings.LocationBarPromptText = settings.LocationBarPromptText ?? null;
                this.TempBCSettings.LocationBarHeaderText = settings.LocationBarHeaderText ?? null;
                this.TempBCSettings.LocationBarExcludedText = settings.LocationBarExcludedText ?? null;
                this.TempBCSettings.PdpMessageSuccess = settings.PdpMessageSuccess ?? null;
                this.TempBCSettings.PdpMessageConditional = settings.PdpMessageConditional ?? null;
                this.TempBCSettings.PdpMessageFflConsumer = settings.PdpMessageFflConsumer ?? null;
                this.TempBCSettings.PdpMessageFflB2bProvided = settings.PdpMessageFflB2bProvided ?? null;
                this.TempBCSettings.PdpMessageFflB2bNotProvided = settings.PdpMessageFflB2bNotProvided ?? null;
                this.TempBCSettings.PdpMessageCatalogError = settings.PdpMessageCatalogError ?? null;
                this.TempBCSettings.PdpExcludedText = settings.PdpExcludedText ?? null;
                this.TempBCSettings.RestrictedPrefix = settings.RestrictedPrefix ?? null;
                this.TempBCSettings.PassStatusEnabled = settings.PassStatusEnabled ?? false;
                this.TempBCSettings.PassStatusId = settings.PassStatusId ?? null;
                this.TempBCSettings.FailStatusEnabled = settings.FailStatusEnabled ?? false;
                this.TempBCSettings.FailStatusId = settings.FailStatusId ?? null;
                this.TempBCSettings.PostOrderTriggerMode = settings.PostOrderTriggerMode ?? 0;
                this.TempBCSettings.PostOrderTriggerStatuses = this.toArray(settings.PostOrderTriggerStatuses, this.Statuses, "id");
            }
            else
            {
                this.TempBCSettings.ID = null
                this.TempBCSettings.Hash = null
                this.TempBCSettings.StoreName = null
                this.TempBCSettings.BCAccessToken = null
                this.TempBCSettings.StoreChannelID = null
                this.TempBCSettings.SubscriberServiceFlowIDPreCheckout = null
                this.TempBCSettings.SubscriberServiceFlowIDPostCheckout =  null
                this.TempBCSettings.SubscriberServiceFlowIDClearShop =  null
                this.TempBCSettings.OrderStatusUpdate = null
                this.TempBCSettings.OrderComment = null
                this.TempBCSettings.PreCheckoutEnabled = null
                this.TempBCSettings.DisplayCondlFail = null
                this.TempBCSettings.DisplayCustomerType = null
                this.TempBCSettings.DisplayLicenseMap = null
                this.TempBCSettings.Fee = {Enabled: false, Minimum: 0, Maximum: 0, Percent: 0, CountDuplicates: 0}
                this.TempBCSettings.AnchorElementTypeID = null
                this.TempBCSettings.AnchorElementValue = null
                this.TempBCSettings.AnchorElementPositionID = null
                this.TempBCSettings.ScriptUUID = null
                this.TempBCSettings.ExcludedStates = null;
                this.TempBCSettings.selectedExcludedStates = [];
                this.TempBCSettings.ExcludedCustomerGroups = null;
                this.TempBCSettings.selectedExcludedCustomerGroups = [];
                this.TempBCSettings.AllowedStatuses = [];
                this.TempBCSettings.StatusGuard = false;
                this.TempBCSettings.AutoCancel = false;
                this.TempBCSettings.ClearShopEnabled = false;
                this.TempBCSettings.CacheCleared = null;
                this.TempBCSettings.AllowedDomain = null;
                this.TempBCSettings.LocationBarPromptText = null;
                this.TempBCSettings.LocationBarHeaderText = null;
                this.TempBCSettings.LocationBarExcludedText = null;
                this.TempBCSettings.PdpMessageSuccess = null;
                this.TempBCSettings.PdpMessageConditional = null;
                this.TempBCSettings.PdpMessageFflConsumer = null;
                this.TempBCSettings.PdpMessageFflB2bProvided = null;
                this.TempBCSettings.PdpMessageFflB2bNotProvided = null;
                this.TempBCSettings.PdpMessageCatalogError = null;
                this.TempBCSettings.PdpExcludedText = null;
                this.TempBCSettings.RestrictedPrefix = null;
                this.TempBCSettings.PassStatusEnabled = false;
                this.TempBCSettings.PassStatusId = null;
                this.TempBCSettings.FailStatusEnabled = false;
                this.TempBCSettings.FailStatusId = null;
                this.TempBCSettings.PostOrderTriggerMode = 0;
                this.TempBCSettings.PostOrderTriggerStatuses = [];
            }
        },
        GetOptions(method, body, token)
        {
            return {
                method: method,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Auth-Token': token,
                    'Access-Control-Allow-Origin': "Null"
                },
                body: method === "GET" ? null : JSON.stringify(body)
            };
        },
        ValidateSettings()
        {
            if(this.Adding)
                return {
                    ID:                                 this.Editing.ID,
                    Hash:                               this.TempBCSettings.Hash,
                    StoreName:                          this.TempBCSettings.StoreName,
                    BCAccessToken:                      this.TempBCSettings.BCAccessToken,
                    StoreChannelID:                     this.TempBCSettings.StoreChannelID || "1",
                    SubscriberServiceFlowIDPreCheckout: this.TempBCSettings.SubscriberServiceFlowIDPreCheckout,
                    SubscriberServiceFlowIDPostCheckout:this.TempBCSettings.SubscriberServiceFlowIDPostCheckout,
                    SubscriberServiceFlowIDClearShop:   this.TempBCSettings.SubscriberServiceFlowIDClearShop,
                    OrderStatusUpdate:                  this.TempBCSettings.OrderStatusUpdate,
                    OrderComment:                       this.TempBCSettings.OrderComment,
                    DisplayCondlFail:                   this.TempBCSettings.DisplayCondlFail,
                    DisplayCustomerType:                this.TempBCSettings.DisplayCustomerType,
                    DisplayLicenseMap:                  this.TempBCSettings.DisplayLicenseMap,
                    PreCheckoutEnabled:                 this.TempBCSettings.PreCheckoutEnabled,
                    Fee:                                this.FixedFee(),
                    AnchorElementTypeID:                this.TempBCSettings.AnchorElementTypeID,
                    AnchorElementValue:                 this.TempBCSettings.AnchorElementValue,
                    AnchorElementPositionID:            this.TempBCSettings.AnchorElementPositionID,
                    ScriptUUID:                         null,
                    ExcludedStates:                     this.NamesToString(this.TempBCSettings.selectedExcludedStates),
                    ExcludedCustomerGroups:             this.IDsToString(this.TempBCSettings.selectedExcludedCustomerGroups),
                    AllowedStatuses:                    this.IDsToString(this.TempBCSettings.AllowedStatuses),
                    StatusGuard:                        this.TempBCSettings.StatusGuard,
                    AutoCancel:                         this.TempBCSettings.AutoCancel,
                    ClearShopEnabled:                   this.TempBCSettings.ClearShopEnabled,
                    CacheCleared:                       this.TempBCSettings.CacheCleared,
                    AllowedDomain:                      this.TempBCSettings.AllowedDomain,
                    LocationBarPromptText:              this.TempBCSettings.LocationBarPromptText,
                    LocationBarHeaderText:              this.TempBCSettings.LocationBarHeaderText,
                    LocationBarExcludedText:            this.TempBCSettings.LocationBarExcludedText,
                    PdpMessageSuccess:                  this.TempBCSettings.PdpMessageSuccess,
                    PdpMessageConditional:              this.TempBCSettings.PdpMessageConditional,
                    PdpMessageFflConsumer:              this.TempBCSettings.PdpMessageFflConsumer,
                    PdpMessageFflB2bProvided:           this.TempBCSettings.PdpMessageFflB2bProvided,
                    PdpMessageFflB2bNotProvided:        this.TempBCSettings.PdpMessageFflB2bNotProvided,
                    PdpMessageCatalogError:             this.TempBCSettings.PdpMessageCatalogError,
                    PdpExcludedText:                    this.TempBCSettings.PdpExcludedText,
                    RestrictedPrefix:                   this.TempBCSettings.RestrictedPrefix,
                    PassStatusEnabled:                  this.TempBCSettings.PassStatusEnabled,
                    PassStatusId:                       this.TempBCSettings.PassStatusId,
                    FailStatusEnabled:                  this.TempBCSettings.FailStatusEnabled,
                    FailStatusId:                       this.TempBCSettings.FailStatusId,
                    PostOrderTriggerMode:               this.TempBCSettings.PostOrderTriggerMode,
                    PostOrderTriggerStatuses:           this.IDsToString(this.TempBCSettings.PostOrderTriggerStatuses),
                }
            else
                return {
                    ID:                                 this.Editing.ID,
                    Hash:                               this.Editing.Hash,
                    StoreName:                          this.TempBCSettings.StoreName,
                    BCAccessToken:                      this.Editing.BCAccessToken,
                    StoreChannelID:                     this.TempBCSettings.StoreChannelID || "1",
                    SubscriberServiceFlowIDPreCheckout: this.TempBCSettings.SubscriberServiceFlowIDPreCheckout,
                    SubscriberServiceFlowIDPostCheckout:this.TempBCSettings.SubscriberServiceFlowIDPostCheckout,
                    SubscriberServiceFlowIDClearShop:   this.TempBCSettings.SubscriberServiceFlowIDClearShop,
                    OrderStatusUpdate:                  this.TempBCSettings.OrderStatusUpdate,
                    OrderComment:                       this.TempBCSettings.OrderComment,
                    DisplayCondlFail:                   this.TempBCSettings.DisplayCondlFail,
                    DisplayCustomerType:                this.TempBCSettings.DisplayCustomerType,
                    DisplayLicenseMap:                  this.TempBCSettings.DisplayLicenseMap,
                    PreCheckoutEnabled:                 this.TempBCSettings.PreCheckoutEnabled,
                    Fee:                                this.FixedFee(),
                    AnchorElementTypeID:                this.TempBCSettings.AnchorElementTypeID,
                    AnchorElementValue:                 this.TempBCSettings.AnchorElementValue,
                    AnchorElementPositionID:            this.TempBCSettings.AnchorElementPositionID,
                    ScriptUUID:                         this.Editing.ScriptUUID,
                    ExcludedStates:                     this.NamesToString(this.TempBCSettings.selectedExcludedStates),
                    ExcludedCustomerGroups:             this.IDsToString(this.TempBCSettings.selectedExcludedCustomerGroups),
                    AllowedStatuses:                    this.IDsToString(this.TempBCSettings.AllowedStatuses),
                    StatusGuard:                        this.TempBCSettings.StatusGuard,
                    AutoCancel:                         this.TempBCSettings.AutoCancel,
                    ClearShopEnabled:                   this.TempBCSettings.ClearShopEnabled,
                    CacheCleared:                       this.TempBCSettings.CacheCleared,
                    AllowedDomain:                      this.TempBCSettings.AllowedDomain,
                    LocationBarPromptText:              this.TempBCSettings.LocationBarPromptText,
                    LocationBarHeaderText:              this.TempBCSettings.LocationBarHeaderText,
                    LocationBarExcludedText:            this.TempBCSettings.LocationBarExcludedText,
                    PdpMessageSuccess:                  this.TempBCSettings.PdpMessageSuccess,
                    PdpMessageConditional:              this.TempBCSettings.PdpMessageConditional,
                    PdpMessageFflConsumer:              this.TempBCSettings.PdpMessageFflConsumer,
                    PdpMessageFflB2bProvided:           this.TempBCSettings.PdpMessageFflB2bProvided,
                    PdpMessageFflB2bNotProvided:        this.TempBCSettings.PdpMessageFflB2bNotProvided,
                    PdpMessageCatalogError:             this.TempBCSettings.PdpMessageCatalogError,
                    PdpExcludedText:                    this.TempBCSettings.PdpExcludedText,
                    RestrictedPrefix:                   this.TempBCSettings.RestrictedPrefix,
                    PassStatusEnabled:                  this.TempBCSettings.PassStatusEnabled,
                    PassStatusId:                       this.TempBCSettings.PassStatusId,
                    FailStatusEnabled:                  this.TempBCSettings.FailStatusEnabled,
                    FailStatusId:                       this.TempBCSettings.FailStatusId,
                    PostOrderTriggerMode:               this.TempBCSettings.PostOrderTriggerMode,
                    PostOrderTriggerStatuses:           this.IDsToString(this.TempBCSettings.PostOrderTriggerStatuses),
                }
        },
        FixedFee()
        {
            const fixed = {}
            fixed.Enabled = this.TempBCSettings.Fee.Enabled
            fixed.Minimum = parseFloat(this.TempBCSettings.Fee.Minimum)
            fixed.Maximum = parseFloat(this.TempBCSettings.Fee.Maximum)
            fixed.Percent = parseFloat(this.TempBCSettings.Fee.Percent)
            fixed.CountDuplicates = parseInt(this.TempBCSettings.Fee.CountDuplicates)
            return fixed
        },
        saveHandle()
        {
            document.getElementById("bcForm").requestSubmit();
        },
        async SelectEdit(settings)
        {
            this.ChannelsLoaded = false;
            this.ChannelsLoading = false;
            this.ShowChannelPicker = false;
            this.UpdateTemp(settings);
            this.Editing = {...settings, Fee: {...settings.Fee}};
            if (this.Editing.Hash && this.Editing.BCAccessToken)
            {
                await this.getBigCommerceCustomerGroups({ hash: this.Editing.Hash, token: this.Editing.BCAccessToken });
                this.TempBCSettings.selectedExcludedCustomerGroups = this.TempBCSettings.ExcludedCustomerGroups
                    ? this.toIDArray(this.TempBCSettings.ExcludedCustomerGroups, this.BCCustomerGroups, "id")
                    : [];
            }
        },
        NewStore()
        {
            const settings = {
                ID: 0,
                Hash: null,
                StoreName: null,
                BCAccessToken: null,
                StoreChannelID: null,
                SubscriberServiceFlowIDPreCheckout: null,
                SubscriberServiceFlowIDPostCheckout:  null,
                SubscriberServiceFlowIDClearShop:  null,
                OrderStatusUpdate: false,
                OrderComment: false,
                PreCheckoutEnabled: false,
                DisplayCondlFail: false,
                DisplayCustomerType: false,
                DisplayLicenseMap: false,
                Fee: {Enabled: false, Minimum: 0, Maximum: 0, Percent: 0, CountDuplicates: 0},
                AnchorElementTypeID: null,
                AnchorElementValue: null,
                AnchorElementPositionID: null,
                ScriptUUID: null,
                ExcludedStates: null,
                ExcludedCustomerGroups: null,
                AllowedStatuses: "4,5,6,13",
                StatusGuard: false,
                AutoCancel: false,
                PassStatusEnabled: false,
                PassStatusId: null,
                FailStatusEnabled: false,
                FailStatusId: null,
                PostOrderTriggerMode: 0,
                PostOrderTriggerStatuses: "",
                    ClearShopEnabled: false,
                CacheCleared: null,
                AllowedDomain: null,
                LocationBarPromptText: null,
                LocationBarHeaderText: null,
                LocationBarExcludedText: null,
                PdpMessageSuccess: null,
                PdpMessageConditional: null,
                PdpMessageFflConsumer: null,
                PdpMessageFflB2bProvided: null,
                PdpMessageFflB2bNotProvided: null,
                PdpMessageCatalogError: null,
                PdpExcludedText: null,
                RestrictedPrefix: null,
            }
            this.Adding = true;
            this.ChannelsLoaded = false;
            this.ChannelsLoading = false;
            this.ShowChannelPicker = false;
            this.UpdateTemp(settings);
            this.DefaultSingleFlowSelections();
            this.Editing = {...settings, Fee: {...settings.Fee}};
        },
        //#region delete store
        ConfirmDelete(settings)
        {
            this.Deleting = settings;
        },
        async DeleteRow(settings)
        {
            await this.deleteBigCommerce(settings);
            await this.getAllBigCommerce();
            this.Deleting = null;
        },
        //#endregion
        //#region validation
        ValidateFields()
        {
            const toCheck = ["StoreName", "PreFlow", "PostFlow", "AnchorType", "AnchorElement", "AnchorPosition"];
            if(this.Adding)
                toCheck.push("StoreHash", "BCAccessToken");

            let agg = true;
            for(const field of toCheck)
                agg = this.FieldCheck(field) && agg;
            return agg;
        },
        FieldCheck(field)
        {
            const elem = document.getElementById(field);
            elem.classList.remove("needed");
            if(!document.getElementById(field).value)
            {
                elem.classList.add("needed");
                return false;
            }
            return true;
        },
        // Max-length check for the optional ClearShop messaging fields (empty/null is allowed).
        // Returns the display labels of any field exceeding MessageMaxLength.
        MessageLengthErrors()
        {
            const fields = [
                { key: "LocationBarPromptText",       label: "Location Bar Prompt Text" },
                { key: "LocationBarHeaderText",       label: "Location Bar Header Text" },
                { key: "LocationBarExcludedText",     label: "Location Bar Excluded Text" },
                { key: "PdpMessageSuccess",           label: "PDP Success Message" },
                { key: "PdpMessageConditional",       label: "PDP Conditional Message" },
                { key: "PdpMessageFflConsumer",       label: "PDP FFL Consumer Message" },
                { key: "PdpMessageFflB2bProvided",    label: "PDP FFL B2B Provided Message" },
                { key: "PdpMessageFflB2bNotProvided", label: "PDP FFL B2B Not Provided Message" },
                { key: "PdpMessageCatalogError",      label: "PDP Catalog Error Message" },
                { key: "PdpExcludedText",             label: "PDP Excluded Text" },
                { key: "RestrictedPrefix",            label: "Restricted Prefix" },
            ];
            return fields
                .filter(f => (this.TempBCSettings[f.key] ?? "").length > this.MessageMaxLength)
                .map(f => f.label);
        },
        //#endregion
        NamesToString(list)
        {
            let stringList = null;
            if (list && list.length > 0)
                stringList = list.map(s => s.name).join(',');
            return stringList;
        },
        IDsToString(list)
        {
            let stringList = null;
            if (list && list.length > 0)
                stringList = list.map(s => s.id).join(',');
            return stringList;
        },
        toArray(list, arrToMatch, attrToMatch)
        {
            if (list)
            {
                const selectedValues = list.split(',');
                return arrToMatch.filter(x => selectedValues.includes(x[attrToMatch]));
            }
            else
            {
                return [];
            }
        },
        toIDArray(csv, masterList, key)
        {
            if (!csv) return [];
            const ids = csv.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
            return masterList.filter(item => ids.includes(item[key]));
        },
        async LoadChannels()
        {
            if (!this.TempBCSettings.Hash || !this.TempBCSettings.BCAccessToken) return;
            this.ChannelsLoading = true;
            await this.getBigCommerceChannels({ hash: this.TempBCSettings.Hash, token: this.TempBCSettings.BCAccessToken });
            await this.getBigCommerceCustomerGroups({ hash: this.TempBCSettings.Hash, token: this.TempBCSettings.BCAccessToken });
            this.ChannelsLoaded = true;
            this.ChannelsLoading = false;

            if (!this.BCChannelsError && this.AvailableChannels.length > 0)
            {
                this.ShowChannelPicker = true;
                this.TempBCSettings.StoreChannelID = String(this.AvailableChannels[0].id);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
thead
{
    :first-child
    {
        width: 200px;
        border-top: none;
    }
    :nth-child(2)
    {
        width: 150px;
        border-top: none;
    }
    :nth-child(3)
    {
        width:max-content;
        padding-left: 15px;
        border-top: none;
    }
    :nth-child(4)
    {
        width: 75px;
        padding-left: .5em;
        border-top: none;
        text-align: center;
    }
}

td 
{
    padding: 0;
}

tbody:last-child
{   
    tr
    {
        border-bottom:none;
    }
}

.tableText
{
    font-size: 12pt;
    padding-left: 15px;
}

select, .form-control
{
    width: 24em;
}

.OptionSwitch
{
    width: fit-content;
    display: inline-block;
}

label
{
    width: 16em;
    vertical-align: middle;
    font-weight: 600;
}

/* Align messaging-field labels with the first line of text in their textareas */
.clearshop-messages label
{
    vertical-align: top;
    padding-top: calc(0.375rem + 1px);
}

/* Let the messaging textareas fill the rest of the row beside their 20em labels
   (0.5em cushion absorbs the inline gap between label and textarea) */
.clearshop-messages textarea.form-control
{
    width: calc(100% - 16em);
    vertical-align: top;
}

.form-control
{
    display: inline-block;
    margin-bottom: .5em;
}

.right-align
{
    text-align: right;
    width: 7em;
}

#SaveButton
{
    position: absolute;
    width: max-content;
    right: 1em;
    bottom: 1em;
    > div 
    {
        display: inline-block;
    }
    > button
    {
        margin-right: 1em;
    }
}

#percentSym
{
    width:fit-content;
}

.currency
{
    display: inline-block;
    position: relative;
}

.currency::before
{
    position: absolute;
    top: 20%;
    content:"$";
    left: 5px;
}

.percentage
{
    display: inline-block;
    position: relative;
}

.percentage::after
{
    position: absolute;
    top: 20%;
    content:"%";
    right: 15px;
}

.percentage:hover::after, .percentage:focus-within::after
{
    display: none;
}

.needed
{
    outline: 4px solid #FF6666;
}
</style>